<template>
    <div class="bg-gray-50 rounded-lg shadow-sm flex flex-col fading-out-highlight">
        <div class="flex flex-row justify-between gap-6 pr-2 bg-gray-100 rounded-t-lg">
            <div class="flex-1 py-1">
                <div ref="tabHeader">
                    <slot v-bind="{ headlineId: props.headlineId }" name="header" />
                </div>
            </div>

            <ul role="tablist" aria-orientation="horizontal" class="flex flex-row gap-2 items-end pt-1">
                <CardTabNavigationItem
                    v-for="(item, index) in props.items"
                    :key="index"
                    :is-focused="focusedTabIndex === index"
                    :is-manual-focus-change="isManualFocusChange"
                    :is-active="activeSyncId === item.syncId"
                    :sync-id="item.syncId"
                    :tab-panel-id="item.tabPanelId"
                    :icon="item.icon"
                    @focus-first-tab="focusFirstTab"
                    @focus-last-tab="focusLastTab"
                    @focus-previous-tab="focusPreviousTab"
                    @focus-next-tab="focusNextTab"
                    @focus-tab="(isManualFocusChange: boolean) => focusTab(item.syncId, index, isManualFocusChange)"
                    @focus-tab-panel="emit('focus-tab-panel')"
                >
                    {{ item.label }}
                </CardTabNavigationItem>
            </ul>
        </div>

        <div class="bg-gray-50 border-t border-t-gray-200 rounded-b-lg">
            <slot name="body" :active-sync-id="activeSyncId" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import CardTabNavigationItem, { CardTabNavigationItemProps } from './CardTabNavigationItem.vue';
import { useCardTabNavigation } from './composables/useCardTabNavigation';

export type CardTabNavigationItemType = Pick<CardTabNavigationItemProps, 'tabPanelId' | 'icon'> & {
    label: string;
    syncId: string;
};

type CardTabNavigationProps = {
    headlineId: string;
    items: CardTabNavigationItemType[];
};

const props = defineProps<CardTabNavigationProps>();
const emit = defineEmits<{
    (e: 'focus-tab-panel'): void;
}>();

const route = useRoute();

const { activeSyncId, setActiveSyncId } = useCardTabNavigation();

onMounted(() => {
    setActiveSyncId(props.items[0].syncId);
});

const lastTabIndex = computed(() => props.items.length - 1);
const focusedTabIndex = ref(-1);
const isManualFocusChange = ref(false);

function focusFirstTab() {
    focusedTabIndex.value = 0;
    isManualFocusChange.value = true;
}

function focusLastTab() {
    focusedTabIndex.value = lastTabIndex.value;
    isManualFocusChange.value = true;
}

function focusPreviousTab() {
    if (focusedTabIndex.value > 0) {
        focusedTabIndex.value = focusedTabIndex.value - 1;
        isManualFocusChange.value = true;
    }
}

function focusNextTab() {
    if (focusedTabIndex.value < lastTabIndex.value) {
        focusedTabIndex.value = focusedTabIndex.value + 1;
        isManualFocusChange.value = true;
    }
}

function focusTab(syncId: string, tabIndex: number, isManualChange: boolean) {
    setActiveSyncId(syncId);
    focusedTabIndex.value = tabIndex;
    isManualFocusChange.value = isManualChange;
}

const tabHeader = useTemplateRef('tabHeader');

watchEffect(() => {
    const hash = route.hash.replace('#', '');
    if (!hash || !tabHeader.value) return;

    const target = tabHeader.value.querySelector(`#${hash}`);
    if (target) {
        nextTick(() => {
            target.scrollIntoView({ block: 'center', behavior: 'smooth' });
            target.classList.add('is-card-headline-target');
        });
    }
});
</script>

<style>
.fading-out-highlight:has(.is-card-headline-target),
.fading-out-highlight:has(:target) {
    animation: ease-in-out fading-out-border 5s;
}

@keyframes fading-out-border {
    0% {
        box-shadow: 0 0 0 0.25rem var(--color-sky-700);
    }

    100% {
        box-shadow: var(--shadow-sm); /* === @apply shadow-sm which isn't allowed inside of a @keyframes rule */
    }
}
</style>
